/* ==========================================================================
   Cart Drawer V2 — native AJAX add-to-cart confirmation drawer

   Item rows are rendered in Liquid by sections/cart-drawer-items.liquid and
   fetched via the Section Rendering API — edit the row markup THERE, not here.
   This file only handles open/close, add-to-cart interception, quantity
   stepping (MOQ + multiples from data attributes) and injecting the section.

   Markup: snippets/cart-drawer-v2.liquid  |  Styles: assets/cart-drawer.css
   ========================================================================== */

(function () {
  'use strict';

  var ITEMS_SECTION_ID = 'cart-drawer-items';
  var UPSELLS_SECTION_ID = 'cart-drawer-upsells';
  var AUTO_CLOSE_DELAY = 12000; // ms — auto-dismiss after add-to-cart if untouched

  var drawer, panel, itemsEl, emptyEl, footerEl, titleEl, subtotalPriceEl, subtotalLabelEl, upsellsEl, savingsRowEl, savingsPriceEl;
  var strings = {};
  var busy = false;
  var lastFocused = null;
  var autoCloseTimer = null;

  // The cart page (sections/main-cart-v2.liquid) handles its own add-to-cart
  // and re-renders in place — the drawer must not open there.
  function onCartPage() {
    return !!document.getElementById('CartPageV2');
  }

  document.addEventListener('DOMContentLoaded', init);

  function init() {
    drawer = document.getElementById('CartDrawerV2');
    if (!drawer) return;

    panel = drawer.querySelector('.cart-drawer-v2__panel');
    itemsEl = drawer.querySelector('[data-cdv2-items]');
    emptyEl = drawer.querySelector('[data-cdv2-empty]');
    footerEl = drawer.querySelector('[data-cdv2-footer]');
    titleEl = drawer.querySelector('[data-cdv2-title]');
    subtotalPriceEl = drawer.querySelector('[data-cdv2-subtotal]');
    subtotalLabelEl = drawer.querySelector('[data-cdv2-subtotal-label]');
    savingsRowEl = drawer.querySelector('[data-cdv2-savings-row]');
    savingsPriceEl = drawer.querySelector('[data-cdv2-savings]');
    upsellsEl = drawer.querySelector('[data-cdv2-upsells]');

    var stringsEl = document.getElementById('CartDrawerV2Strings');
    if (stringsEl) {
      try {
        strings = JSON.parse(stringsEl.textContent);
      } catch (e) {
        strings = {};
      }
    }

    // Close triggers (X button + overlay)
    drawer.querySelectorAll('[data-cdv2-close]').forEach(function (el) {
      el.addEventListener('click', close);
    });

    document.addEventListener('keydown', function (evt) {
      if (evt.key === 'Escape' && drawer.classList.contains('is-open')) {
        close();
      }
    });

    // Any interaction with the panel cancels the auto-close timer
    ['pointerenter', 'pointerdown', 'focusin', 'touchstart'].forEach(function (evtName) {
      panel.addEventListener(evtName, cancelAutoClose, { passive: true });
    });

    // Header cart icon → open drawer instead of navigating to /cart.
    // On the cart page (CartPageV2) the drawer stays disabled — let the link
    // navigate normally and the cart page handle its own adds.
    document.addEventListener('click', function (evt) {
      if (onCartPage()) return;
      var trigger = evt.target.closest('.js-drawer-open-cart');
      if (!trigger) return;
      evt.preventDefault();
      openFromIcon();
    });

    // AJAX add-to-cart for forms that contain a .js-add-to-cart button.
    // Skipped on the cart page so adds there refresh the page instead.
    document.addEventListener('submit', function (evt) {
      if (onCartPage()) return;
      var form = evt.target;
      if (!form.querySelector || !form.querySelector('.js-add-to-cart')) return;
      evt.preventDefault();
      addFromForm(form);
    });

    // Qty +/- and remove (delegated inside drawer)
    itemsEl.addEventListener('click', onItemsClick);

    // Re-assert the savings value whenever DN mutates the drawer — either
    // injecting its per-line discount (items) or overwriting our footer row.
    // The diff-check in syncDrawerDnSavings prevents observer feedback loops.
    if (window.MutationObserver) {
      var syncObserver = new MutationObserver(function () {
        window.requestAnimationFrame(syncDrawerDnSavings);
      });
      syncObserver.observe(itemsEl, { childList: true, subtree: true, characterData: true });
      if (footerEl) {
        syncObserver.observe(footerEl, { childList: true, subtree: true, characterData: true });
      }
    }

    // Populate the drawer once on load so the first open is instant.
    refreshDrawer();
  }

  /*= Add to cart ==========================================================*/

  function addFromForm(form) {
    if (busy) return;
    busy = true;

    var button = form.querySelector('.js-add-to-cart');
    if (button) button.classList.add('btn--loading');
    clearFormErrors(form);

    fetch(routes().cartAdd, {
      method: 'POST',
      body: new FormData(form),
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (response) { return response.json(); })
      .then(function (data) {
        busy = false;
        if (button) button.classList.remove('btn--loading');

        if (data.status === 422 || data.status === 404) {
          showFormError(form, data.description || data.message || '');
          return;
        }

        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: { product: data, addToCartBtn: button }
        }));

        // loadUpsells(data.handle); // "You may also like" temporarily disabled

        refreshDrawer().then(function () {
          if (titleEl && strings.addedTitle) titleEl.textContent = strings.addedTitle;
          open();
          startAutoClose();
        });
      })
      .catch(function () {
        busy = false;
        if (button) button.classList.remove('btn--loading');
      });
  }

  function clearFormErrors(form) {
    var errors = form.querySelector('.errors');
    if (errors) errors.remove();
  }

  function showFormError(form, message) {
    if (!message) return;
    var errorDiv = document.createElement('div');
    errorDiv.classList.add('errors', 'text-center');
    errorDiv.textContent = message;
    form.append(errorDiv);
  }

  /*= Open / close =========================================================*/

  function openFromIcon() {
    if (titleEl && strings.cartTitle) titleEl.textContent = strings.cartTitle;
    // The drawer already holds the last rendered contents, so open instantly
    // and refresh in the background.
    open();
    refreshDrawer();
  }

  function open() {
    if (drawer.classList.contains('is-open')) return;
    lastFocused = document.activeElement;
    drawer.classList.add('is-visible');
    drawer.setAttribute('aria-hidden', 'false');
    // double rAF so the visibility change lands before the transform kicks in
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        drawer.classList.add('is-open');
      });
    });
    document.documentElement.classList.add('cdv2-open');
    var closeBtn = drawer.querySelector('.cart-drawer-v2__close');
    if (closeBtn) closeBtn.focus({ preventScroll: true });
  }

  function close() {
    cancelAutoClose();
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('cdv2-open');
    window.setTimeout(function () {
      drawer.classList.remove('is-visible');
    }, 450);
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus({ preventScroll: true });
      lastFocused = null;
    }
  }

  function startAutoClose() {
    cancelAutoClose();
    autoCloseTimer = window.setTimeout(close, AUTO_CLOSE_DELAY);
  }

  function cancelAutoClose() {
    if (autoCloseTimer) {
      window.clearTimeout(autoCloseTimer);
      autoCloseTimer = null;
    }
  }

  /*= Rendering (Liquid section) ===========================================*/

  function sectionUrl(id) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root + '?section_id=' + id;
  }

  // Fetch sections/cart-drawer-items.liquid and inject the rows + summary.
  function refreshDrawer() {
    return fetch(sectionUrl(ITEMS_SECTION_ID), { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('items fetch failed');
        return response.text();
      })
      .then(applyContents)
      .catch(function () {});
  }

  function applyContents(html) {
    var wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    var data = wrapper.querySelector('[data-cdv2-data]');
    if (!data) return;

    var count = parseInt(data.getAttribute('data-count'), 10) || 0;
    updateHeaderBubble(count);

    if (count === 0) {
      itemsEl.innerHTML = '';
      emptyEl.hidden = false;
      footerEl.hidden = true;
      return;
    }

    emptyEl.hidden = true;
    footerEl.hidden = false;

    itemsEl.innerHTML = data.innerHTML;
    subtotalPriceEl.textContent = data.getAttribute('data-subtotal') || '';
    subtotalLabelEl.textContent = subtotalLabel(count);

    if (savingsRowEl && savingsPriceEl) {
      if (data.getAttribute('data-has-savings') === '1') {
        savingsPriceEl.textContent = '−' + (data.getAttribute('data-savings') || '');
        savingsRowEl.hidden = false;
      } else {
        // Always clear any stale value (e.g. a previous DN top-up) so it can't
        // survive into a cart state that no longer has savings.
        savingsPriceEl.textContent = '';
        savingsRowEl.hidden = true;
      }
    }

    // Bases for the savings calc. DN's automatic rule lands in Shopify cart
    // data with a delay: when it has, data-cart-discount already covers it and
    // there is nothing to add; when it hasn't, we add DN's per-line amount.
    drawerBase = {
      saving: parseInt(data.getAttribute('data-savings-cents'), 10) || 0,
      cartDiscount: parseInt(data.getAttribute('data-cart-discount'), 10) || 0
    };
    syncDrawerDnSavings();
    window.setTimeout(syncDrawerDnSavings, 800);
    window.setTimeout(syncDrawerDnSavings, 2000);
    window.setTimeout(syncDrawerDnSavings, 4000);

    // Tell Discount Ninja to re-scan: the drawer rows were injected via JS
    // after DN's initial pass, so without this DN only adds its per-line text
    // and doesn't replace the price (540 → 486 strike) like it does on the
    // cart page. This is the same event the theme uses for dynamically loaded
    // products (Searchanise). rAF so the new nodes are in the DOM first.
    window.requestAnimationFrame(function () {
      document.dispatchEvent(new CustomEvent('limoniapps:discountninja:collection:productsadded'));
    });

    document.dispatchEvent(new CustomEvent('cartDrawerV2:updated'));
  }

  /*= Discount Ninja savings (authoritative) ===============================*/
  // The theme OWNS the "Your savings" value. It is always computed from fixed
  // inputs — compare-at/base savings + DN's per-line amount not yet in cart
  // data — and re-asserted, so any stale value DN writes into the row is
  // overwritten rather than trusted. Hidden whenever the result is <= 0.

  var drawerBase = null;

  function parseMoneyCents(text) {
    if (!text) return null;
    var t = String(text).replace(/[’']/g, '').replace(/[−–]/g, '-');
    var m = t.match(/CHF\s*([\d.,]+)/i);
    if (!m) return null;
    var v = parseFloat(m[1].replace(/,(?=\d{3}(\D|$))/g, ''));
    return isNaN(v) ? null : Math.round(v * 100);
  }

  function dnDrawerCents() {
    var sum = 0;
    if (!itemsEl) return 0;
    itemsEl.querySelectorAll('[data-la-dn-cart-item-text]').forEach(function (el) {
      var got = 0;
      var all = el.querySelectorAll('*');
      for (var i = 0; i < all.length && !got; i++) {
        var attrs = all[i].attributes;
        for (var j = 0; j < attrs.length; j++) {
          if (/price-?cents?$/i.test(attrs[j].name)) {
            var v = parseInt(attrs[j].value, 10);
            if (v > 0) { got = v; break; }
          }
        }
      }
      if (!got) got = parseMoneyCents(el.textContent) || 0;
      sum += got;
    });
    return sum;
  }

  function formatMoney(cents) {
    if (window.theme && window.theme.Currency && window.theme.settings) {
      return window.theme.Currency.formatMoney(cents, window.theme.settings.moneyFormat);
    }
    return 'CHF ' + (cents / 100).toFixed(2);
  }

  function drawerSavingsTarget() {
    if (!drawerBase) return 0;
    var topup = dnDrawerCents() - drawerBase.cartDiscount;
    if (topup < 0) topup = 0;
    return drawerBase.saving + topup;
  }

  // Read the DN "You save" amount (line total) from one row's text anchor.
  function dnRowSaveCents(row) {
    var el = row.querySelector('[data-la-dn-cart-item-text]');
    if (!el) return 0;
    var all = el.querySelectorAll('*');
    for (var i = 0; i < all.length; i++) {
      var attrs = all[i].attributes;
      for (var j = 0; j < attrs.length; j++) {
        if (/price-?cents?$/i.test(attrs[j].name)) {
          var v = parseInt(attrs[j].value, 10);
          if (v > 0) return v;
        }
      }
    }
    return parseMoneyCents(el.textContent) || 0;
  }

  // DN in the drawer only adds its "You save" text and does NOT replace the
  // price (540 → 486 strike) like it does on the cart page. So render that
  // compare/sale style ourselves from DN's per-line amount.
  function applyDnPriceStyle() {
    if (!itemsEl) return;
    itemsEl.querySelectorAll('[data-cdv2-key]').forEach(function (row) {
      var priceWrap = row.querySelector('[data-la-dn-cart-item-hide-if-dynamic-pricing-isshown]');
      if (!priceWrap) return;

      var unit = parseInt(row.getAttribute('data-cdv2-unit-price'), 10) || 0;
      var qty = parseInt(row.getAttribute('data-cdv2-qty'), 10) || 1;
      var save = dnRowSaveCents(row);
      var discounted = unit - Math.round(save / qty);

      // No DN discount (or a compare-at item DN already excludes) → leave the
      // theme price untouched.
      if (!unit || save <= 0 || discounted <= 0 || discounted >= unit) return;

      var stamp = unit + ':' + discounted;
      if (priceWrap.getAttribute('data-cdv2-dnstyled') === stamp) return;
      priceWrap.setAttribute('data-cdv2-dnstyled', stamp);
      priceWrap.innerHTML =
        '<span class="cart-drawer-v2__price-compare">' + formatMoney(unit) + '</span> ' +
        '<span class="cart-drawer-v2__sale">' + formatMoney(discounted) + '</span>';
    });
  }

  function syncDrawerDnSavings() {
    applyDnPriceStyle();
    if (!savingsRowEl || !savingsPriceEl) return;
    var val = drawerSavingsTarget();
    if (val > 0) {
      var text = '−' + formatMoney(val);
      if (savingsPriceEl.textContent !== text) savingsPriceEl.textContent = text;
      if (savingsRowEl.hidden) savingsRowEl.hidden = false;
    } else {
      if (savingsPriceEl.textContent !== '') savingsPriceEl.textContent = '';
      if (!savingsRowEl.hidden) savingsRowEl.hidden = true;
    }
  }

  /*= Upsells ==============================================================*/

  // Renders sections/cart-drawer-upsells.liquid in the context of the
  // just-added product; a first-hit locale redirect can drop the section_id,
  // so retry once when the expected container is missing.
  function loadUpsells(handle, attempt) {
    if (!upsellsEl || !handle) return;
    attempt = attempt || 0;
    if (attempt === 0) hideUpsells();

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url = root + 'products/' + encodeURIComponent(handle) + '?section_id=' + UPSELLS_SECTION_ID;

    fetch(url, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('upsells fetch failed');
        return response.text();
      })
      .then(function (html) {
        var wrapper = document.createElement('div');
        wrapper.innerHTML = html;
        var inner = wrapper.querySelector('.cart-drawer-v2__upsells-inner');
        if (inner) {
          upsellsEl.innerHTML = '';
          upsellsEl.appendChild(inner);
          upsellsEl.hidden = false;
        } else if (attempt < 1) {
          loadUpsells(handle, attempt + 1);
        }
      })
      .catch(function () {
        if (attempt < 1) {
          loadUpsells(handle, attempt + 1);
        } else {
          hideUpsells();
        }
      });
  }

  function hideUpsells() {
    if (!upsellsEl) return;
    upsellsEl.hidden = true;
    upsellsEl.innerHTML = '';
  }

  /*= Quantity change (MOQ + multiples from row data attributes) ===========*/

  function routes() {
    var r = (window.theme && window.theme.routes) || {};
    return {
      cartAdd: r.cartAdd || '/cart/add.js',
      cartChange: r.cartChange || '/cart/change.js'
    };
  }

  function onItemsClick(evt) {
    var minus = evt.target.closest('[data-cdv2-minus]');
    var plus = evt.target.closest('[data-cdv2-plus]');
    var remove = evt.target.closest('[data-cdv2-remove]');
    var row = evt.target.closest('[data-cdv2-key]');
    if (!row) return;

    // We handle qty/remove ourselves via AJAX (drawer stays open). Stop the
    // click here so Discount Ninja's drawer module (bound higher up via the
    // la-dn-drawercart-root marker) can't hijack it and reload the page.
    if (minus || plus || remove) {
      evt.preventDefault();
      evt.stopPropagation();
    }

    var key = row.getAttribute('data-cdv2-key');
    var qty = parseInt(row.getAttribute('data-cdv2-qty'), 10) || 0;
    var step = parseInt(row.getAttribute('data-cdv2-multiple'), 10) || 1;
    var min = parseInt(row.getAttribute('data-cdv2-moq'), 10) || step;

    if (remove) {
      changeItem(key, 0);
    } else if (plus) {
      changeItem(key, nextQty(qty, min, step));
    } else if (minus) {
      var down = prevQty(qty, min, step);
      if (down < qty) changeItem(key, down);
    }
  }

  // Next valid quantity above qty (valid = min + k * step; off-grid snaps up).
  function nextQty(qty, min, step) {
    if (qty < min) return min;
    return min + (Math.floor((qty - min) / step) + 1) * step;
  }

  // Previous valid quantity below qty, never below min (9 → 8 → 4 / min 4 step 4).
  function prevQty(qty, min, step) {
    if (qty <= min) return min;
    var offset = (qty - min) % step;
    var down = offset === 0 ? qty - step : qty - offset;
    return Math.max(down, min);
  }

  function changeItem(key, quantity) {
    if (busy) return;
    busy = true;
    itemsEl.classList.add('is-busy');

    fetch(routes().cartChange, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (response) { return response.json(); })
      .then(function (data) {
        // A cart object has items; an error payload (e.g. max stock) does not.
        if (!data || !Array.isArray(data.items)) {
          showDrawerError(data && data.description);
        }
        return refreshDrawer();
      })
      .then(function () {
        busy = false;
        itemsEl.classList.remove('is-busy');
      })
      .catch(function () {
        busy = false;
        itemsEl.classList.remove('is-busy');
        refreshDrawer();
      });
  }

  var drawerErrorTimer = null;

  function showDrawerError(message) {
    if (!message || !footerEl) return;
    var errorEl = footerEl.querySelector('.cart-drawer-v2__error');
    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.className = 'cart-drawer-v2__error';
      footerEl.prepend(errorEl);
    }
    errorEl.textContent = message;
    if (drawerErrorTimer) window.clearTimeout(drawerErrorTimer);
    drawerErrorTimer = window.setTimeout(function () {
      errorEl.remove();
    }, 5000);
  }

  /*= Helpers ==============================================================*/

  function subtotalLabel(count) {
    var itemsText = count === 1 ? (strings.itemOne || '') : (strings.itemOther || '');
    itemsText = itemsText.replace('[count]', count);
    var label = strings.subtotal || 'Subtotal';
    return itemsText ? label + ' (' + itemsText + ')' : label;
  }

  function updateHeaderBubble(count) {
    document.querySelectorAll('.cart-link__bubble').forEach(function (bubble) {
      bubble.classList.toggle('cart-link__bubble--visible', count > 0);
      bubble.textContent = count > 0 ? String(count) : '';
    });
  }
})();
